/** Persist / restore human-workflow question position per session (browser sessionStorage). */

export type JourneyResumePointer = {
  domainKey: string
  questionKey: string
  controlKey?: string
  stepIndex?: number
}

export function hasSavedWorkflowAnswer(value?: string | null): boolean {
  const av = String(value ?? '').trim().toLowerCase()
  return av.length > 0 && av !== 'null' && av !== 'undefined'
}

export function journeyResumeStorageKey(sessionId: string, standardCode: string, surface: string): string {
  return `cc-journey-resume:${sessionId}:${standardCode}:${surface}`
}

export function readJourneyResumePointer(key: string): JourneyResumePointer | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as JourneyResumePointer
    if (!parsed?.domainKey || !parsed?.questionKey) return null
    return parsed
  } catch {
    return null
  }
}

export function writeJourneyResumePointer(key: string, pointer: JourneyResumePointer): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(pointer))
  } catch {
    /* quota / private mode */
  }
}

export function findFirstUnansweredStepIndex(
  steps: Array<{ domainKey: string; questionKey: string; answerValue?: string | null }>,
): number {
  if (!steps.length) return 0
  const idx = steps.findIndex((s) => !hasSavedWorkflowAnswer(s.answerValue))
  return idx >= 0 ? idx : Math.max(0, steps.length - 1)
}

export function resolveControlCentricResumeIndex(
  steps: Array<{
    domainKey: string
    questionKey: string
    answerValue?: string | null
  }>,
  saved: JourneyResumePointer | null,
): number {
  if (!steps.length) return 0
  const firstOpen = findFirstUnansweredStepIndex(steps)
  if (!saved) return firstOpen
  const byKey = steps.findIndex(
    (s) => s.domainKey === saved.domainKey && s.questionKey === saved.questionKey,
  )
  if (byKey < 0) return firstOpen
  if (!hasSavedWorkflowAnswer(steps[byKey]?.answerValue)) return byKey
  return firstOpen
}

export function resolveFlatJourneyResumeIndex(
  flatSteps: Array<{ domainKey: string; questionKey: string }>,
  responses: Array<{ questionKey?: string; domainKey?: string; answerValue?: unknown }> | undefined,
  saved: JourneyResumePointer | null,
): number {
  if (!flatSteps.length) return 0
  const answered = new Set(
    (responses || [])
      .filter((r) => hasSavedWorkflowAnswer(r.answerValue as string))
      .map((r) => `${String(r.domainKey)}::${String(r.questionKey)}`),
  )
  const firstOpen = flatSteps.findIndex((s) => !answered.has(`${s.domainKey}::${s.questionKey}`))
  const firstIdx = firstOpen >= 0 ? firstOpen : Math.max(0, flatSteps.length - 1)
  if (!saved) return firstIdx
  const byKey = flatSteps.findIndex(
    (s) => s.domainKey === saved.domainKey && s.questionKey === saved.questionKey,
  )
  if (byKey < 0) return firstIdx
  if (!answered.has(`${saved.domainKey}::${saved.questionKey}`)) return byKey
  return firstIdx
}
